import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["allocationAmount", "interestAmount", "principalAmount", "feeAmount", "remainingAmount"]

  static values = {
    interestDue: Number,
    principalDue: Number,
    remainingBase: Number,
    remainingDisplayId: String
  }

  connect() {
    this.syncing = false
    this.updateRemainingAmount()
  }

  allocationAmountChanged() {
    if (this.syncing) return

    const amount = this.parseAmount(this.allocationAmountTarget.value)
    if (amount === null) {
      this.setBucketAmounts(null, null, null)
      this.updateRemainingAmount()
      return
    }

    const split = this.splitAmount(amount)
    this.setBucketAmounts(split.interest, split.principal, split.fee)
    this.updateRemainingAmount()
  }

  bucketAmountChanged() {
    if (this.syncing) return

    const total = [this.interestAmountTarget, this.principalAmountTarget, this.feeAmountTarget]
      .map((target) => this.parseAmount(target.value) || 0)
      .reduce((sum, amount) => sum + amount, 0)

    this.syncing = true
    this.setInputValue(this.allocationAmountTarget, total)
    this.syncing = false
    this.updateRemainingAmount()
  }

  useScheduled(event) {
    event.preventDefault()

    this.syncing = true
    this.setInputValue(this.allocationAmountTarget, this.parseAmount(event.params.amount))
    this.setInputValue(this.interestAmountTarget, this.parseAmount(event.params.interest))
    this.setInputValue(this.principalAmountTarget, this.parseAmount(event.params.principal))
    this.setInputValue(this.feeAmountTarget, this.parseAmount(event.params.fee))
    this.syncing = false
    this.updateRemainingAmount()
  }

  splitAmount(amount) {
    const interest = Math.min(amount, this.interestDueValue || 0)
    const principal = Math.min(amount - interest, this.principalDueValue || 0)
    const fee = amount - interest - principal

    return { interest, principal, fee }
  }

  setBucketAmounts(interest, principal, fee) {
    this.syncing = true
    this.setInputValue(this.interestAmountTarget, interest)
    this.setInputValue(this.principalAmountTarget, principal)
    this.setInputValue(this.feeAmountTarget, fee)
    this.syncing = false
  }

  setInputValue(input, amount) {
    const value = amount === null || amount === undefined ? "" : amount.toFixed(2)
    input.value = value
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateRemainingAmount() {
    const remainingAmountElement = this.remainingAmountElement
    if (!remainingAmountElement) return

    const allocationAmount = this.hasAllocationAmountTarget ? this.parseAmount(this.allocationAmountTarget.value) || 0 : 0
    const remainingAmount = (this.remainingBaseValue || 0) - allocationAmount
    remainingAmountElement.textContent = this.formatCurrency(remainingAmount)
    remainingAmountElement.classList.toggle("text-error", remainingAmount < 0)
  }

  get remainingAmountElement() {
    if (this.hasRemainingAmountTarget) return this.remainingAmountTarget
    if (this.hasRemainingDisplayIdValue) return document.getElementById(this.remainingDisplayIdValue)

    return null
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(amount)
  }

  parseAmount(value) {
    if (value === null || value === undefined || value === "") return null

    const normalized = value.toString().replace(/[^\d.-]/g, "")
    if (normalized === "") return null

    const amount = Number.parseFloat(normalized)
    return Number.isNaN(amount) ? null : amount
  }
}
